// ════════════════════════════════════════════════════════════════════════════
// ConfirmDialog — confirmación común para las acciones del panel que dan, quitan
// o publican algo (cristales, bonos, referidos, borrados, anuncios).
//
// Uso:
//   const [confirm, confirmDialog] = useConfirm();
//   if (!(await confirm({ title: 'Transferir cristales', body: <>…</>, confirmLabel: 'Transferir' }))) return;
//   …
//   return <>{confirmDialog}…</>;
// ════════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useRef, useState } from 'react';

const TONES = {
  default: { bg: 'rgba(0,229,255,0.12)',   border: 'rgba(0,229,255,0.35)',   color: '#00e5ff' },
  danger:  { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.35)', color: '#f87171' },
  success: { bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.35)',  color: '#34d399' },
};

export function useConfirm() {
  const [state, setState] = useState(null); // { title, body, confirmLabel, tone, resolve }

  const confirm = useCallback((opts) => new Promise((resolve) => {
    setState({ confirmLabel: 'Confirmar', tone: 'default', ...opts, resolve });
  }), []);

  const close = (result) => {
    state?.resolve(result);
    setState(null);
  };

  const dialog = state ? <ConfirmDialog {...state} onCancel={() => close(false)} onConfirm={() => close(true)} /> : null;
  return [confirm, dialog];
}

export default function ConfirmDialog({ title, body, confirmLabel = 'Confirmar', tone = 'default', onCancel, onConfirm }) {
  const t = TONES[tone] || TONES.default;
  const confirmRef = useRef(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      role="presentation"
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        data-testid="confirm-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'rgba(5,15,35,0.98)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 14, padding: 'clamp(18px, 4vw, 28px)', maxWidth: 440, width: '100%' }}
      >
        <p id="confirm-dialog-title" style={{ color: 'rgba(224,247,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, margin: '0 0 12px', textTransform: 'uppercase' }}>
          {title}
        </p>
        <div style={{ color: '#e0e8f0', fontSize: 14, lineHeight: 1.55, margin: '0 0 22px', overflowWrap: 'anywhere' }}>
          {body}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="confirm-dialog-cancel"
            style={{ padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(224,247,255,0.6)', cursor: 'pointer' }}
          >
            Cancelar
          </button>
          <button
            type="button"
            ref={confirmRef}
            onClick={onConfirm}
            data-testid="confirm-dialog-ok"
            style={{ padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, background: t.bg, border: `1px solid ${t.border}`, color: t.color, cursor: 'pointer' }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
